// app/frontend/theme/index.ts
// Centralized theme configuration for consistent styling across the app

import { createElement } from 'react';
import { MantineThemeOverride, createTheme, MantineSize, Loader, LoadingOverlay } from '@mantine/core';
import { TinyDanceLoader } from '../components/ui/Loaders/TinyDanceLoader';
import { DanceLoader } from '../components/ui/Loaders/DanceLoader';
import { COLORS } from '../src/constants/theme';

// =============================================================================
// COLOR PALETTE
// =============================================================================
export const colors = {
  // Brand colors
  primary: '#EA580C', // Orange
  primaryLight: 'rgba(234, 88, 12, 0.1)',
  primaryDark: '#c44a0a',

  // Background colors
  background: {
    dark: '#171717',
    card: '#17171770',
    cardSolid: '#222222',
    input: '#2c2c2c',
    hover: '#2a2a2b',
  },

  // Text colors
  text: {
    primary: '#f2f2f2',
    secondary: '#9CA3AF',
    dimmed: '#757575',
    white: 'white',
    muted: '#f2f2f270',
  },

  // Border colors
  border: {
    default: '#444444',
    light: '#404040',
    divider: '#757575',
  },

  // Status colors
  status: {
    success: '#2ecc71',
    warning: '#f2c94c',
    error: '#e74c3c',
    info: '#3498db',
    inactive: '#828282',
  },
} as const;

// =============================================================================
// RESPONSIVE SIZING
// =============================================================================

// Text sizes for different contexts
export const textSizes = {
  // Page titles (e.g., "Edit Event", "Edit Artist")
  pageTitle: { mobile: 'lg', desktop: 'xl' },

  // Section/Accordion titles
  sectionTitle: { mobile: 'md', desktop: 'lg' },

  // Accordion panel header text (smaller than section title)
  accordionTitle: { mobile: 'sm', desktop: 'md' },

  // Card/item titles
  itemTitle: { mobile: 'sm', desktop: 'md' },

  // Body text
  body: { mobile: 'xs', desktop: 'sm' },

  // Small/caption text
  caption: { mobile: 'xs', desktop: 'xs' },

  // Form labels
  label: { mobile: 'xs', desktop: 'sm' },

  // Form helper/description text
  helper: { mobile: 'xs', desktop: 'xs' },

  // Status/badge text
  status: { mobile: 'xs', desktop: 'xs' },
} as const;

// Font sizes in rem/px for inline styles (use sparingly, prefer Mantine sizes)
export const fontSizes = {
  accordionTitle: { mobile: '1rem', desktop: '1.125rem' },
  sectionHeader: { mobile: '0.875rem', desktop: '1rem' },
  inputLabel: { mobile: '0.75rem', desktop: '0.875rem' },
} as const;

// Icon sizes
export const iconSizes = {
  xs: { mobile: 12, desktop: 14 },
  sm: { mobile: 14, desktop: 16 },
  md: { mobile: 16, desktop: 18 },
  lg: { mobile: 18, desktop: 20 },
  xl: { mobile: 20, desktop: 24 },
} as const;

// Button sizes
export const buttonSizes = {
  sm: { mobile: 'xs', desktop: 'sm' },
  md: { mobile: 'sm', desktop: 'md' },
  lg: { mobile: 'md', desktop: 'lg' },
} as const;

// Spacing
export const spacing = {
  section: { mobile: 'md', desktop: 'xl' },
  card: { mobile: 'xs', desktop: 'sm' },
  item: { mobile: 'xs', desktop: 'md' },
  gap: { mobile: 4, desktop: 8 },
} as const;

// Badge sizes
export const badgeSizes = {
  default: { mobile: 'xs', desktop: 'sm' },
} as const;

// Action icon sizes
export const actionIconSizes = {
  sm: { mobile: 24, desktop: 28 },
  md: { mobile: 26, desktop: 30 },
  lg: { mobile: 30, desktop: 36 },
} as const;

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get responsive value based on mobile flag
 */
export function responsive<T>(values: { mobile: T; desktop: T }, isMobile: boolean): T {
  return isMobile ? values.mobile : values.desktop;
}

/**
 * Get responsive text size
 */
export function getTextSize(
  key: keyof typeof textSizes,
  isMobile: boolean
): MantineSize {
  return responsive(textSizes[key], isMobile) as MantineSize;
}

/**
 * Get responsive icon size
 */
export function getIconSize(
  key: keyof typeof iconSizes,
  isMobile: boolean
): number {
  return responsive(iconSizes[key], isMobile);
}

/**
 * Get responsive button size
 */
export function getButtonSize(
  key: keyof typeof buttonSizes,
  isMobile: boolean
): MantineSize {
  return responsive(buttonSizes[key], isMobile) as MantineSize;
}

/**
 * Get responsive spacing
 */
export function getSpacing(
  key: keyof typeof spacing,
  isMobile: boolean
): string | number {
  return responsive(spacing[key], isMobile);
}

/**
 * Get responsive badge size
 */
export function getBadgeSize(isMobile: boolean): MantineSize {
  return responsive(badgeSizes.default, isMobile) as MantineSize;
}

/**
 * Get responsive action icon size
 */
export function getActionIconSize(
  key: keyof typeof actionIconSizes,
  isMobile: boolean
): number {
  return responsive(actionIconSizes[key], isMobile);
}

/**
 * Get responsive font size (for inline styles)
 */
export function getFontSize(
  key: keyof typeof fontSizes,
  isMobile: boolean
): string {
  return responsive(fontSizes[key], isMobile);
}

// =============================================================================
// MANTINE THEME OVERRIDE
// =============================================================================
export const wcwdTheme: MantineThemeOverride = createTheme({
  primaryColor: 'orange',
  fontFamily: 'Poppins, sans-serif',

  colors: {
    // Custom orange color scale
    orange: [
      '#fff7ed', // 0 - lightest
      '#ffedd5', // 1
      '#fed7aa', // 2
      '#fdba74', // 3
      '#fb923c', // 4
      '#f97316', // 5
      '#ea580c', // 6 - primary
      '#c2410c', // 7
      '#9a3412', // 8
      '#7c2d12', // 9 - darkest
    ],
  },

  components: {
    // Every Mantine spinner (loading buttons, ActionIcons, ThemedSpinner,
    // LoadingOverlay) is a tiny dance loader: equalizer, beat dots or
    // footsteps, picked at random (#1885).
    Loader: Loader.extend({
      defaultProps: {
        loaders: { ...Loader.defaultLoaders, dance: TinyDanceLoader },
        type: 'dance',
      },
    }),
    // Overlays have room for the full count, so they get the big loader.
    LoadingOverlay: LoadingOverlay.extend({
      defaultProps: {
        loaderProps: { children: createElement(DanceLoader) },
      },
    }),
    Text: {
      defaultProps: {
        c: colors.text.primary,
      },
    },
    Paper: {
      defaultProps: {
        bg: colors.background.card,
      },
    },
    // The provider runs in Mantine's light scheme, so striped rows, row hover
    // and cell borders would fall back to light grays. Same tokens as
    // AdminTable (#2503).
    Table: {
      defaultProps: {
        stripedColor: COLORS.whiteAlpha06,
        highlightOnHoverColor: COLORS.orangeAlpha10,
        borderColor: COLORS.whiteAlpha10,
      },
    },
    Divider: {
      defaultProps: {
        color: colors.border.divider,
      },
    },
    Anchor: {
      defaultProps: {
        c: 'orange',
      },
    },
    Menu: {
      // Site-wide dark menu treatment (Linear / Cal.com pattern).
      // Dropdown surface sits one shade darker than card bg so it detaches
      // visually; items are 13px with 6px hover radius and hairline dividers.
      defaultProps: {
        shadow: 'md',
        radius: 'md',
        withinPortal: true,
      },
      styles: {
        dropdown: {
          background: '#18181B',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          borderRadius: 8,
          padding: 4,
          boxShadow: '0 12px 32px rgba(0,0,0,0.5), 0 2px 8px rgba(0,0,0,0.3)',
        },
        item: {
          fontSize: 13,
          fontWeight: 500,
          color: '#d1d1d1',
          borderRadius: 6,
          padding: '7px 10px',
          lineHeight: 1.4,
        },
        itemSection: {
          opacity: 0.7,
        },
        label: {
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: '#999999',
          padding: '8px 10px 4px',
        },
        divider: {
          borderColor: 'rgba(255, 255, 255, 0.06)',
          margin: '4px 2px',
        },
      },
    },
  },
});

// =============================================================================
// COMMON STYLES
// =============================================================================
export const commonStyles = {
  // Dark input style (for forms)
  darkInput: {
    backgroundColor: colors.background.input,
    color: colors.text.white,
    border: `1px solid ${colors.border.default}`,
  },

  // Card style
  darkCard: {
    backgroundColor: colors.background.card,
    border: `1px solid ${colors.border.default}`,
    borderRadius: '8px',
  },

  // Solid card style
  solidCard: {
    backgroundColor: colors.background.cardSolid,
    borderRadius: '8px',
  },
} as const;

// Export everything as default for convenience
export default {
  colors,
  textSizes,
  fontSizes,
  iconSizes,
  buttonSizes,
  spacing,
  badgeSizes,
  actionIconSizes,
  responsive,
  getTextSize,
  getFontSize,
  getIconSize,
  getButtonSize,
  getSpacing,
  getBadgeSize,
  getActionIconSize,
  wcwdTheme,
  commonStyles,
};
